import { CreateLogger } from "@swivl-mono/helpers";
import { EscalationInterface, Log, OnlineRosterEntry, SessionInterface, UserInterface } from "@swivl-mono/types";

import { UsersModelStoreInterface } from "../Users.Model";

const log = CreateLogger(Log.USERS, "debounce");

type Id = number;

type Dated = { id: number; updatedAt?: Date | string | number };

const waitMs = 500;

export class UsersModelDebounceManager {
  static toTs(v?: Date | string | number): number {
    if (v == null) return -Infinity;
    if (v instanceof Date) return v.getTime();
    const n = typeof v === "string" ? Date.parse(v) : Number(v);
    return Number.isFinite(n) ? n : -Infinity;
  }

  static mergeLatest<T extends Dated>(...lists: Array<ReadonlyArray<T> | undefined>): T[] {
    const byId = new Map<Id, T>();
    for (const list of lists) {
      if (!list) continue;
      for (const item of list) {
        if (!item || item.id == null) continue;
        const prev = byId.get(item.id);
        if (!prev || UsersModelDebounceManager.toTs(item.updatedAt) >= UsersModelDebounceManager.toTs(prev.updatedAt)) {
          byId.set(item.id, item);
        }
      }
    }
    return [...byId.values()];
  }

  // Merge usersOnline maps by userId, latest lastSeenMs wins
  static mergeUsersOnline(...maps: Array<{ [userId: number]: OnlineRosterEntry } | undefined>): { [userId: number]: OnlineRosterEntry } | undefined {
    const result: { [userId: number]: OnlineRosterEntry } = {};
    for (const map of maps) {
      if (!map) continue;
      for (const [userIdStr, entry] of Object.entries(map)) {
        const userId = Number(userIdStr);
        const existing = result[userId];
        if (!existing || entry.lastSeenMs > existing.lastSeenMs) {
          result[userId] = entry;
        }
      }
    }
    return Object.keys(result).length ? result : undefined;
  }

  static createUsersModelThrottledSetter(
    setter: (
      users?: UserInterface[],
      sessions?: SessionInterface[],
      escalations?: EscalationInterface[],
      additionalData?: Partial<UsersModelStoreInterface>,
      usersOnline?: { [userId: number]: OnlineRosterEntry }
    ) => void
  ) {
    let interval: ReturnType<typeof setInterval> | null = null;
    let active = false;
    let lastInputTs = 0;

    // Accumulated pending payload
    let pendingUsers: UserInterface[] | undefined;
    let pendingSessions: SessionInterface[] | undefined;
    let pendingEscalations: EscalationInterface[] | undefined;
    let pendingExtra: any | undefined;
    let pendingUsersOnline: { [userId: number]: OnlineRosterEntry } | undefined;

    function hasPending(): boolean {
      return Boolean(
        (pendingUsers && pendingUsers.length) ||
          (pendingSessions && pendingSessions.length) ||
          (pendingEscalations && pendingEscalations.length) ||
          (pendingExtra && Object.keys(pendingExtra).length) ||
          (pendingUsersOnline && Object.keys(pendingUsersOnline).length)
      );
    }

    function accumulate(
      users?: UserInterface[],
      sessions?: SessionInterface[],
      escalations?: EscalationInterface[],
      extra?: UsersModelStoreInterface,
      usersOnline?: { [userId: number]: OnlineRosterEntry }
    ) {
      lastInputTs = Date.now();
      pendingUsers = UsersModelDebounceManager.mergeLatest<UserInterface>(pendingUsers, users);
      pendingSessions = UsersModelDebounceManager.mergeLatest<SessionInterface>(pendingSessions, sessions);
      pendingEscalations = UsersModelDebounceManager.mergeLatest<EscalationInterface>(pendingEscalations, escalations);
      pendingExtra = { ...(pendingExtra ?? {}), ...(extra ?? {}) };
      pendingUsersOnline = UsersModelDebounceManager.mergeUsersOnline(pendingUsersOnline, usersOnline);
    }

    function flushPending() {
      if (!hasPending()) return;
      log("#### UsersModelDebounceManager.flushPending", {
        pendingUsers,
        pendingSessions,
        pendingEscalations,
        pendingExtra,
        pendingUsersOnline
      });
      setter(
        pendingUsers && pendingUsers.length ? pendingUsers : undefined,
        pendingSessions && pendingSessions.length ? pendingSessions : undefined,
        pendingEscalations && pendingEscalations.length ? pendingEscalations : undefined,
        pendingExtra && Object.keys(pendingExtra).length ? pendingExtra : undefined,
        pendingUsersOnline && Object.keys(pendingUsersOnline).length ? pendingUsersOnline : undefined
      );
      pendingUsers = pendingSessions = pendingEscalations = undefined;
      pendingExtra = undefined;
      pendingUsersOnline = undefined;
    }

    function startIntervalIfNeeded() {
      if (interval) return;
      interval = setInterval(() => {
        // Guaranteed tick every waitMs
        if (hasPending()) {
          log("#### UsersModelDebounceManager.flushPending (interval)");
          flushPending();
        } else {
          const idleFor = Date.now() - lastInputTs;
          if (idleFor >= waitMs) {
            stopInterval();
          }
        }
      }, waitMs);
      active = true;
    }

    function stopInterval() {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      active = false;
    }

    function throttled(
      users?: UserInterface[],
      sessions?: SessionInterface[],
      escalations?: EscalationInterface[],
      extra?: UsersModelStoreInterface,
      usersOnline?: { [userId: number]: OnlineRosterEntry }
    ) {
      const firstOfBurst = !active;
      accumulate(users, sessions, escalations, extra, usersOnline);

      if (firstOfBurst) {
        log("#### UsersModelDebounceManager.throttled (firstOfBurst)");
        flushPending();
        startIntervalIfNeeded();
      }
    }

    throttled.flush = () => {
      log("#### UsersModelDebounceManager.throttled.flush");
      flushPending();
    };

    throttled.cancel = () => {
      stopInterval();
      pendingUsers = pendingSessions = pendingEscalations = undefined;
      pendingExtra = undefined;
      pendingUsersOnline = undefined;
    };

    return throttled as typeof setter & { flush: () => void; cancel: () => void };
  }
}
